import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  ImagePlus,
  Megaphone,
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  Power,
  X,
  BarChart3,
  CalendarDays,
  Globe2,
  MapPin,
  Loader2,
  Check,
  ChevronDown,
  LayoutGrid,
  FileText,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Type,
  Link2,
  Users,
  Send,
} from "lucide-react";
import { postersService, buildPosterImageUrl } from "../../../services/posters.service";

const BORDER = "#f2c6cc";
const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const POSTER_TITLE_LIMIT = 20;
const POSTER_DESCRIPTION_LIMIT = 50;
const POSTER_TIME_ZONE = "Asia/Kolkata";

const POSTER_CONTEXT_OPTIONS = [
  { label: "Grocery", value: "GROCERY" },
  { label: "Food", value: "FOOD" },
  { label: "Grocery + Food", value: "ALL" },
];

const emptyFilters = {
  contexts: ["GROCERY"],
  warehouseIds: [],
  cities: [],
  states: [],
};

const initialForm = {
  title: "",
  description: "",
  ctaLink: "",
  targetType: "ALL",
  startAtLocal: "",
  endAtLocal: "",
  isActive: true,
  image: null,
  filters: emptyFilters,
  placement: "TOP",
};

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[24px] border bg-white shadow-sm ${className}`}
      style={{ borderColor: BORDER }}
    >
      {children}
    </div>
  );
}

function ModalShell({
  open,
  title,
  subtitle,
  children,
  onClose,
  width = "max-w-[980px]",
  zIndex = "z-[1200]",
}) {
  if (!open) return null;

  return (
    <div className={`fixed inset-0 ${zIndex} flex items-center justify-center bg-black/45 px-4 py-6`}>
      <div
        className={`relative w-full ${width} max-h-[92vh] overflow-hidden rounded-[28px] border bg-white shadow-2xl`}
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6"
          style={{ borderColor: BORDER }}
        >
          <div>
            <h3 className="text-[24px] font-bold text-slate-900">{title}</h3>
            {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[calc(92vh-84px)] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder = "",
  type = "text",
  required = false,
  icon: Icon = null,
  maxLength,
}) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </span>

      <div className="relative">
        {Icon ? (
          <Icon
            size={17}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
        ) : null}

        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`h-[54px] w-full min-w-0 rounded-[16px] border bg-white ${Icon ? "pl-12" : "pl-4"
            } ${maxLength ? "pr-16" : "pr-4"} text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]`}
          style={{ borderColor: "#dfe5ee" }}
        />

        {maxLength ? (
          <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-medium text-slate-400">
            {String(value || "").length}/{maxLength}
          </div>
        ) : null}
      </div>
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder = "",
  maxLength = 500,
  icon: Icon = null,
}) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label}</span>

      <div className="relative">
        {Icon ? (
          <Icon size={17} className="pointer-events-none absolute left-4 top-4 text-slate-400" />
        ) : null}

        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={5}
          maxLength={maxLength}
          className={`w-full min-w-0 resize-none rounded-[16px] border bg-white ${Icon ? "pl-12" : "pl-4"
            } pr-4 pb-8 pt-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]`}
          style={{ borderColor: "#dfe5ee" }}
        />
        <div className="pointer-events-none absolute bottom-3 right-4 text-xs font-medium text-slate-400">
          {String(value || "").length}/{maxLength}
        </div>
      </div>
    </label>
  );
}

function SelectInput({ label, value, onChange, options, icon: Icon = null }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label}</span>

      <div className="relative">
        {Icon ? (
          <Icon
            size={17}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
        ) : null}

        <select
          value={value}
          onChange={onChange}
          className={`h-[54px] w-full min-w-0 appearance-none rounded-[16px] border bg-white ${Icon ? "pl-12" : "pl-4"
            } pr-10 text-sm text-slate-700 outline-none transition focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]`}
          style={{ borderColor: "#dfe5ee" }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <ChevronDown
          size={17}
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
        />
      </div>
    </label>
  );
}

function PremiumMultiSelect({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = "Select options",
  helperText = "",
  disabled = false,
  searchable = true,
}) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const normalizedValue = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value]
  );

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;

    return options.filter((option) =>
      String(option?.label || "")
        .toLowerCase()
        .includes(q)
    );
  }, [options, query]);

  const selectedOptions = useMemo(() => {
    const selectedSet = new Set(normalizedValue);
    return options.filter((option) => selectedSet.has(String(option.value)));
  }, [options, normalizedValue]);

  const filteredValues = useMemo(
    () => filteredOptions.map((opt) => String(opt.value)),
    [filteredOptions]
  );

  const allFilteredSelected =
    filteredValues.length > 0 &&
    filteredValues.every((item) => normalizedValue.includes(item));

  const toggleValue = (nextValue) => {
    const val = String(nextValue);

    if (normalizedValue.includes(val)) {
      onChange(normalizedValue.filter((item) => item !== val));
      return;
    }

    onChange([...normalizedValue, val]);
  };

  const handleSelectAll = () => {
    if (!filteredValues.length) return;

    if (allFilteredSelected) {
      onChange(normalizedValue.filter((item) => !filteredValues.includes(item)));
      return;
    }

    onChange(Array.from(new Set([...normalizedValue, ...filteredValues])));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="relative block space-y-2" ref={wrapperRef}>
      <div className="text-[13px] font-semibold text-slate-700">{label}</div>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex min-h-[52px] w-full items-center justify-between gap-3 rounded-[16px] border bg-white px-3.5 py-3 text-left transition ${disabled ? "cursor-not-allowed opacity-60" : "hover:bg-[#fffafb]"
          }`}
        style={{ borderColor: BORDER }}
      >
        <div className="min-w-0 flex-1">
          {selectedOptions.length ? (
            <div className="flex flex-wrap gap-2">
              {selectedOptions.slice(0, 3).map((item) => (
                <span
                  key={item.value}
                  className="inline-flex max-w-full items-center rounded-full bg-[#fff1f3] px-3 py-1 text-xs font-semibold text-[#c95b69]"
                >
                  <span className="truncate">{item.label}</span>
                </span>
              ))}

              {selectedOptions.length > 3 ? (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  +{selectedOptions.length - 3} more
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-sm text-slate-400">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-2 pl-2">
          {selectedOptions.length ? (
            <span className="rounded-full bg-[#fff4f6] px-2.5 py-1 text-xs font-semibold text-[#d86a78]">
              {selectedOptions.length}
            </span>
          ) : null}
          <ChevronDown
            size={16}
            className={`text-slate-500 transition ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open ? (
        <div
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-[20px] border bg-white p-3 shadow-[0_12px_40px_rgba(15,23,42,0.08)]"
          style={{ borderColor: BORDER }}
        >
          {searchable ? (
            <div className="relative mb-3">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}`}
                className="w-full rounded-[14px] border bg-white pl-9 pr-3 py-2.5 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#f7d4da]"
                style={{ borderColor: BORDER }}
              />
            </div>
          ) : null}

          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rounded-[12px] bg-[#fff1f3] px-3 py-2 text-xs font-semibold text-[#c95b69] transition hover:bg-[#ffe5ea]"
            >
              {allFilteredSelected ? "Unselect All" : "Select All"}
            </button>

            <button
              type="button"
              onClick={handleClearAll}
              className="rounded-[12px] bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
            >
              Clear All
            </button>
          </div>

          <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const checked = normalizedValue.includes(String(option.value));

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleValue(option.value)}
                    className={`flex w-full items-center justify-between gap-3 rounded-[14px] border px-3 py-2.5 text-left transition ${checked
                      ? "border-[#f2b9c2] bg-[#fff4f6]"
                      : "border-transparent bg-slate-50 hover:bg-[#fffafb]"
                      }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-700">
                        {option.label}
                      </div>
                    </div>

                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-md border transition ${checked
                        ? "border-[#d86a78] bg-[#d86a78] text-white"
                        : "border-slate-300 bg-white text-transparent"
                        }`}
                    >
                      <Check size={13} />
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="rounded-[14px] bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
                No options found
              </div>
            )}
          </div>
        </div>
      ) : null}

      {helperText ? <p className="text-xs text-slate-500">{helperText}</p> : null}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div className="space-y-2.5">
      <div className="text-[13px] font-semibold text-slate-800">{label}</div>

      <div className="flex h-[54px] items-center justify-end">
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative h-8 w-[62px] rounded-full transition ${checked ? "bg-[#c1275b]" : "bg-slate-300"
            }`}
        >
          <span
            className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition ${checked ? "left-[32px]" : "left-1"
              }`}
          />
        </button>
      </div>
    </div>
  );
}

function toIsoFromLocal(localValue) {
  if (!localValue) return null;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function extractWallClockParts(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = "00"] = match;
  return { year, month, day, hour, minute, second };
}

function toLocalInputValue(isoValue) {
  const parts = extractWallClockParts(isoValue);
  if (parts) {
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  }

  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDate(value) {
  const parts = extractWallClockParts(value);
  if (parts) {
    const datePart = `${parts.month}/${parts.day}/${parts.year}`;
    let hour = Number(parts.hour || 0);
    const suffix = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${datePart}, ${hour}:${parts.minute}:${parts.second} ${suffix}`;
  }

  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function getNowWallClockValue() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: POSTER_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(new Date())
    .reduce((acc, part) => {
      if (part.type !== "literal") acc[part.type] = part.value;
      return acc;
    }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

function toComparableWallClock(value) {
  const parts = extractWallClockParts(value);
  if (!parts) return "";
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

function getPosterComputedStatus(item = {}) {
  const backendStatus = String(item.computed_status || item.computedStatus || "").toUpperCase();
  if (["ACTIVE", "INACTIVE", "SCHEDULED", "EXPIRED"].includes(backendStatus)) {
    return backendStatus;
  }

  if (!item.is_active) return "INACTIVE";

  const now = getNowWallClockValue();
  const start = toComparableWallClock(item.start_at);
  const end = toComparableWallClock(item.end_at);

  if (start && start > now) return "SCHEDULED";
  if (end && end < now) return "EXPIRED";
  return "ACTIVE";
}

function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

function StatusBadge({ item, active }) {
  const status = item ? getPosterComputedStatus(item) : active ? "ACTIVE" : "INACTIVE";

  const styles = {
    ACTIVE: "bg-green-100 text-green-700",
    SCHEDULED: "bg-amber-100 text-amber-700",
    EXPIRED: "bg-red-100 text-red-700",
    INACTIVE: "bg-slate-100 text-slate-500",
  };

  const labels = {
    ACTIVE: "Active",
    SCHEDULED: "Scheduled",
    EXPIRED: "Expired",
    INACTIVE: "Inactive",
  };

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${styles[status] || styles.INACTIVE}`}
    >
      {labels[status] || "Inactive"}
    </span>
  );
}

function normalizeArray(value) {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }

  return Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
}

function normalizePosterContexts(value) {
  const raw = normalizeArray(value).map((item) => item.toUpperCase());
  const mapped = raw
    .map((item) => {
      if (["BOTH", "ALL", "GLOBAL"].includes(item)) return "ALL";
      if (["GROCERY", "GROCERIES", "MART", "STORE"].includes(item)) return "GROCERY";
      if (["FOOD", "RESTAURANT", "RESTAURANTS"].includes(item)) return "FOOD";
      return null;
    })
    .filter(Boolean);

  if (mapped.includes("ALL")) return ["ALL"];

  const unique = Array.from(new Set(mapped));
  return unique.length ? unique : ["GROCERY"];
}

function contextLabel(value) {
  const normalized = normalizePosterContexts(value);
  if (normalized.includes("ALL")) return "Grocery + Food";
  if (normalized.includes("FOOD")) return "Food";
  return "Grocery";
}

function formatLabel(value) {
  return String(value || "")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function normalizeFilterOptions(raw = {}) {
  const states = Array.isArray(raw?.states)
    ? raw.states.map((item) =>
      typeof item === "string"
        ? { value: item, label: formatLabel(item) }
        : { value: item?.value, label: item?.label || formatLabel(item?.value) }
    )
    : [];

  const cities = Array.isArray(raw?.cities)
    ? raw.cities.map((item) =>
      typeof item === "string"
        ? { value: item, label: formatLabel(item) }
        : { value: item?.value, label: item?.label || formatLabel(item?.value) }
    )
    : [];

  const warehouses = Array.isArray(raw?.warehouses)
    ? raw.warehouses.map((item) => ({
      value: item?.value,
      label: item?.label,
      meta: item?.meta || {},
    }))
    : [];

  return { states, cities, warehouses };
}

function FiltersText({ filters, optionsMap }) {
  const contexts = normalizePosterContexts(filters?.contexts);
  const warehouseIds = normalizeArray(filters?.warehouseIds);
  const cities = normalizeArray(filters?.cities);
  const states = normalizeArray(filters?.states);

  const parts = [`Section: ${contextLabel(contexts)}`];

  if (states.length) {
    parts.push(`States: ${states.join(", ")}`);
  }

  if (cities.length) {
    parts.push(`Cities: ${cities.join(", ")}`);
  }

  if (warehouseIds.length) {
    const names = warehouseIds.map((id) => optionsMap?.warehouses?.[id] || id);
    parts.push(`Warehouses: ${names.join(", ")}`);
  }

  return parts.join(" • ");
}

function PosterFormModal({ open, mode = "create", item, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    warehouses: [],
    cities: [],
    states: [],
  });

  useEffect(() => {
    if (!open) return;

    let alive = true;
    setLoadingOptions(true);

    postersService
      .getFilterOptions()
      .then((res) => {
        if (!alive) return;
        const raw = res?.data || res;
        const data = raw?.data || raw || {};
        setFilterOptions(normalizeFilterOptions(data));
      })
      .catch(() => {
        if (!alive) return;
        setFilterOptions({
          warehouses: [],
          cities: [],
          states: [],
        });
      })
      .finally(() => {
        if (alive) setLoadingOptions(false);
      });

    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && item) {
      setForm({
        title: item.title || "",
        description: item.description || "",
        ctaLink: item.cta_link || "",
        targetType: item.target_type || "ALL",
        placement: item.placement || "TOP",
        startAtLocal: toLocalInputValue(item.start_at),
        endAtLocal: toLocalInputValue(item.end_at),
        isActive: !!item.is_active,
        image: null,
        filters: {
          contexts: normalizePosterContexts(item.filters?.contexts),
          warehouseIds: normalizeArray(item.filters?.warehouseIds),
          cities: normalizeArray(item.filters?.cities),
          states: normalizeArray(item.filters?.states),
        },
      });
      setPreviewUrl(buildPosterImageUrl(item.image_url));
    } else {
      setForm({
        ...initialForm,
        filters: { ...emptyFilters },
      });
      setPreviewUrl("");
    }

    setError("");
  }, [open, mode, item]);

  const titleText = mode === "edit" ? "Edit Poster" : "Create New Poster";
  const subtitleText =
    mode === "edit"
      ? "Update poster content, targeting, and image details here."
      : "Add content, targeting, and image details to create a new poster.";

  const selectedStates = normalizeArray(form.filters.states).map((s) => s.toLowerCase());
  const selectedCities = normalizeArray(form.filters.cities).map((c) => c.toLowerCase());

  const filteredCityOptions = useMemo(() => {
    if (!selectedStates.length) return filterOptions.cities;

    const cityMap = new Map();

    (filterOptions.warehouses || []).forEach((warehouse) => {
      const state = String(warehouse?.meta?.state || "").trim().toLowerCase();
      const city = String(warehouse?.meta?.city || "").trim();
      if (!city || !state) return;
      if (!selectedStates.includes(state)) return;

      const key = city.toLowerCase();
      if (!cityMap.has(key)) {
        cityMap.set(key, {
          value: key,
          label: city,
        });
      }
    });

    return Array.from(cityMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions.cities, filterOptions.warehouses, selectedStates]);

  const filteredWarehouseOptions = useMemo(() => {
    return (filterOptions.warehouses || []).filter((warehouse) => {
      const state = String(warehouse?.meta?.state || "").trim().toLowerCase();
      const city = String(warehouse?.meta?.city || "").trim().toLowerCase();

      if (selectedStates.length && (!state || !selectedStates.includes(state))) {
        return false;
      }

      if (selectedCities.length && (!city || !selectedCities.includes(city))) {
        return false;
      }

      return true;
    });
  }, [filterOptions.warehouses, selectedStates, selectedCities]);

  const setFilterValue = (key, value) => {
    setForm((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [key]: value,
      },
    }));
  };

  const handleStatesChange = (values) => {
    const nextStates = values;
    const nextStatesLower = nextStates.map((v) => String(v).toLowerCase());

    setForm((prev) => {
      const allowedCities = (filterOptions.warehouses || [])
        .filter((warehouse) => {
          const state = String(warehouse?.meta?.state || "").trim().toLowerCase();
          return nextStatesLower.includes(state);
        })
        .map((warehouse) => String(warehouse?.meta?.city || "").trim().toLowerCase())
        .filter(Boolean);

      const nextCities = normalizeArray(prev.filters.cities).filter((city) =>
        allowedCities.includes(city.toLowerCase())
      );

      const nextCitiesLower = nextCities.map((v) => v.toLowerCase());

      const nextWarehouseIds = normalizeArray(prev.filters.warehouseIds).filter((warehouseId) => {
        const warehouse = (filterOptions.warehouses || []).find((w) => w.value === warehouseId);
        if (!warehouse) return false;

        const warehouseState = String(warehouse?.meta?.state || "").trim().toLowerCase();
        const warehouseCity = String(warehouse?.meta?.city || "").trim().toLowerCase();

        if (nextStates.length && !nextStatesLower.includes(warehouseState)) {
          return false;
        }

        if (nextCities.length && !nextCitiesLower.includes(warehouseCity)) {
          return false;
        }

        return true;
      });

      return {
        ...prev,
        filters: {
          ...prev.filters,
          states: nextStates,
          cities: nextCities,
          warehouseIds: nextWarehouseIds,
        },
      };
    });
  };

  const handleCitiesChange = (values) => {
    const nextCities = values;
    const nextCitiesLower = nextCities.map((v) => String(v).toLowerCase());

    setForm((prev) => {
      const prevStatesLower = normalizeArray(prev.filters.states).map((v) => v.toLowerCase());

      const nextWarehouseIds = normalizeArray(prev.filters.warehouseIds).filter((warehouseId) => {
        const warehouse = (filterOptions.warehouses || []).find((w) => w.value === warehouseId);
        if (!warehouse) return false;

        const warehouseState = String(warehouse?.meta?.state || "").trim().toLowerCase();
        const warehouseCity = String(warehouse?.meta?.city || "").trim().toLowerCase();

        if (prevStatesLower.length && !prevStatesLower.includes(warehouseState)) {
          return false;
        }

        if (nextCities.length && !nextCitiesLower.includes(warehouseCity)) {
          return false;
        }

        return true;
      });

      return {
        ...prev,
        filters: {
          ...prev.filters,
          cities: nextCities,
          warehouseIds: nextWarehouseIds,
        },
      };
    });
  };

  const hasAnyFilterSelected = () => {
    const f = form.filters || emptyFilters;
    return Boolean(
      normalizeArray(f.warehouseIds).length ||
      normalizeArray(f.cities).length ||
      normalizeArray(f.states).length
    );
  };

  const handleSave = async () => {
    setError("");

    if (mode === "create" && !(form.image instanceof File)) {
      setNotice({
        type: "error",
        title: "Validation Error",
        message: "Poster image is required",
      });
      return;
    }

    if (form.targetType === "FILTERED" && !hasAnyFilterSelected()) {
      setNotice({
        type: "error",
        title: "Validation Error",
        message: "At least one filter is required for filtered poster",
      });
      return;
    }

    const startAt = toIsoFromLocal(form.startAtLocal);
    const endAt = toIsoFromLocal(form.endAtLocal);

    if (!endAt) {
      setNotice({
        type: "error",
        title: "Validation Error",
        message: "Expiry date is required",
      });
      return;
    }

    if (startAt && endAt && new Date(endAt) < new Date(startAt)) {
      setNotice({
        type: "error",
        title: "Validation Error",
        message: "End date cannot be earlier than start date",
      });
      return;
    }

    try {
      setSaving(true);

      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        ctaLink: form.ctaLink.trim() || null,
        targetType: form.targetType,
        placement: form.placement,
        filters:
          form.targetType === "FILTERED"
            ? {
              contexts: normalizePosterContexts(form.filters?.contexts),
              warehouseIds: normalizeArray(form.filters?.warehouseIds),
              cities: normalizeArray(form.filters?.cities),
              states: normalizeArray(form.filters?.states),
            }
            : {
              contexts: normalizePosterContexts(form.filters?.contexts),
              warehouseIds: [],
              cities: [],
              states: [],
            },
        isActive: !!form.isActive,
        startAt,
        endAt,
      };

      if (mode === "edit" && item?.id) {
        if (form.image instanceof File) {
          payload.image = form.image;
        }

        await postersService.update(item.id, payload);

        if (Boolean(item.is_active) !== Boolean(form.isActive)) {
          await postersService.setStatus(item.id, Boolean(form.isActive));
        }
      } else {
        payload.image = form.image;
        await postersService.create(payload);
      }

      setNotice({
        type: "success",
        title: mode === "edit" ? "Poster Updated" : "Poster Created",
        message:
          mode === "edit"
            ? "Poster updated successfully."
            : "Poster created successfully.",
      });

      setTimeout(() => {
        onSaved?.();
        onClose?.();
        setNotice(null);
      }, 900);
    } catch (err) {
      setNotice({
        type: "error",
        title: "Unable to Save Poster",
        message: err?.message || "Something went wrong while saving poster.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (

    <>
      <ConfirmPopup
        open={!!notice}
        title={notice?.title || ""}
        message={notice?.message || ""}
        confirmText="OK"
        cancelText=""
        danger={notice?.type === "error"}
        onClose={() => setNotice(null)}
        onConfirm={() => setNotice(null)}
      />
      <ModalShell
        open={open}
        onClose={onClose}
        title={titleText}
        subtitle={subtitleText}
        width="max-w-[1320px]"
      >
        <div className="bg-[#fcfcfd]">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0 border-r px-6 py-6 sm:px-8" style={{ borderColor: "#e8edf4" }}>

              <div className="space-y-6">
                <div className="grid gap-5 md:grid-cols-2">
                  <TextInput
                    label="Poster Title (Optional)"
                    value={form.title}
                    icon={Type}
                    maxLength={POSTER_TITLE_LIMIT}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter poster title"
                  />

                  <TextInput
                    label="CTA Link"
                    value={form.ctaLink}
                    icon={Link2}
                    onChange={(e) => setForm((prev) => ({ ...prev, ctaLink: e.target.value }))}
                    placeholder="https://example.com or /products"
                  />
                </div>

                <TextArea
                  label="Description"
                  value={form.description}
                  icon={FileText}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Poster description"
                  maxLength={POSTER_DESCRIPTION_LIMIT}
                />

                <div className="grid gap-5 md:grid-cols-4">
                  <SelectInput
                    label="Business Section"
                    value={normalizePosterContexts(form.filters?.contexts)[0] || "GROCERY"}
                    icon={LayoutGrid}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          contexts: [e.target.value],
                        },
                      }))
                    }
                    options={POSTER_CONTEXT_OPTIONS}
                  />

                  <SelectInput
                    label="Target Type"
                    value={form.targetType}
                    icon={Users}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        targetType: e.target.value,
                        filters:
                          e.target.value === "ALL"
                            ? { ...emptyFilters, contexts: normalizePosterContexts(prev.filters?.contexts) }
                            : prev.filters,
                      }))
                    }
                    options={[
                      { label: "All Users", value: "ALL" },
                      { label: "Filtered", value: "FILTERED" },
                    ]}
                  />

                  <SelectInput
                    label="Placement"
                    value={form.placement}
                    icon={LayoutGrid}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        placement: e.target.value,
                      }))
                    }
                    options={[
                      { label: "Top Banner", value: "TOP" },
                      { label: "Middle Banner", value: "MIDDLE" },
                      { label: "Bottom Banner", value: "BOTTOM" },
                      // { label: "Category Banner", value: "CATEGORY" },
                    ]}
                  />

                  <Toggle
                    label="Poster Active"
                    checked={form.isActive}
                    onChange={(value) => setForm((prev) => ({ ...prev, isActive: value }))}
                  />
                </div>

                {form.targetType === "FILTERED" ? (
                  <div
                    className="rounded-[22px] border bg-white p-5 shadow-sm"
                    style={{ borderColor: "#e5eaf2" }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                        <Users size={18} />
                      </div>

                      <div className="min-w-0">
                        <div className="text-[16px] font-semibold text-slate-900">
                          Audience Filters
                        </div>
                        <p className="mt-1 max-w-[560px] text-sm leading-6 text-slate-500">
                          Choose state, city, and warehouse using the multi-select.
                          Selected options will remain checked while editing.
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 grid gap-5 md:grid-cols-2">
                      <PremiumMultiSelect
                        label="States"
                        value={form.filters.states}
                        onChange={handleStatesChange}
                        options={filterOptions.states}
                        placeholder="Select states"
                        helperText="After selecting a state, the related cities will be displayed."
                      />

                      <PremiumMultiSelect
                        label="Cities"
                        value={form.filters.cities}
                        onChange={handleCitiesChange}
                        options={filteredCityOptions}
                        placeholder="Select cities"
                        helperText="Cities are filtered according to the selected states."
                      />

                      <div className="md:col-span-2">
                        <PremiumMultiSelect
                          label="Warehouses"
                          value={form.filters.warehouseIds}
                          onChange={(values) => setFilterValue("warehouseIds", values)}
                          options={filteredWarehouseOptions}
                          placeholder="Select warehouses"
                          helperText="Warehouses are filtered according to the selected state and city."
                        />
                      </div>
                    </div>

                    {loadingOptions ? (
                      <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 size={14} className="animate-spin" />
                        Loading options...
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="grid gap-5 md:grid-cols-2">
                  <TextInput
                    label="Start At"
                    type="datetime-local"
                    value={form.startAtLocal}
                    onChange={(e) => setForm((prev) => ({ ...prev, startAtLocal: e.target.value }))}
                  />
                  <TextInput
                    label="End At"
                    required
                    type="datetime-local"
                    value={form.endAtLocal}
                    onChange={(e) => setForm((prev) => ({ ...prev, endAtLocal: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="min-w-0 px-6 py-6">
              <div className="space-y-6">
                <div
                  className="rounded-[22px] border bg-white p-5 shadow-sm"
                  style={{ borderColor: "#e5eaf2" }}
                >
                  <div className="mb-4 flex items-center gap-3 text-[16px] font-semibold text-slate-900">
                    <ImagePlus size={18} />
                    Poster Image
                  </div>

                  <label
                    className="flex min-h-[190px] cursor-pointer flex-col items-center justify-center rounded-[18px] border border-dashed px-5 py-6 text-center transition hover:bg-[#fffafb]"
                    style={{ borderColor: "#f3c8d0", backgroundColor: "#fffdfd" }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Poster preview"
                        className="h-[190px] w-full rounded-[14px] object-cover"
                      />
                    ) : (
                      <>
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                          <ImagePlus size={24} />
                        </div>

                        <div className="text-[16px] font-semibold text-slate-900">
                          Choose poster image
                        </div>
                        <div className="mt-2 text-sm text-slate-500">PNG, JPG, JPEG</div>
                        <div className="mt-1 text-sm text-slate-400">Max size 5MB</div>
                      </>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setForm((prev) => ({ ...prev, image: file }));
                        if (file) {
                          setPreviewUrl(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </label>
                </div>

                <div
                  className="rounded-[22px] border bg-white p-5 shadow-sm"
                  style={{ borderColor: "#e5eaf2" }}
                >
                  <div className="mb-4 flex items-center gap-3 text-[16px] font-semibold text-slate-900">
                    <Eye size={18} />
                    Quick Preview
                  </div>

                  <div
                    className="rounded-[20px] border px-6 py-8 text-center"
                    style={{ borderColor: "#e9edf4", backgroundColor: "#f7f8fc" }}
                  >
                    <div className="mx-auto mb-6 flex h-[92px] w-[92px] items-center justify-center rounded-[18px] bg-[#fbecef] text-[#e3a6b6]">
                      <ImagePlus size={34} />
                    </div>

                    {form.title.trim() ? (
                      <div className="break-words text-[18px] font-bold text-slate-900">
                        {form.title}
                      </div>
                    ) : null}

                    <div className="mt-3 break-words whitespace-pre-wrap text-sm leading-6 text-slate-500">
                      {form.description || "Poster description will appear here"}
                    </div>

                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                      <span className="rounded-full bg-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-600">
                        {form.targetType === "ALL" ? "All Users" : "Filtered"}
                      </span>

                      <span className="rounded-full bg-[#eef3ff] px-4 py-1.5 text-sm font-semibold text-[#4f7cff]">
                        {contextLabel(form.filters?.contexts)}
                      </span>

                      <span
                        className={`rounded-full px-4 py-1.5 text-sm font-semibold ${form.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-200 text-slate-600"
                          }`}
                      >
                        {form.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="flex flex-col-reverse gap-3 border-t bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
            style={{ borderColor: "#e8edf4" }}
          >
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-[50px] items-center justify-center rounded-[14px] border bg-white px-6 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              style={{ borderColor: "#dfe5ee" }}
            >
              Cancel
            </button>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex h-[50px] items-center justify-center gap-2 rounded-[14px] px-6 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
                style={{ backgroundColor: PINK }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = PINK_HOVER)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = PINK)}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {saving ? "Saving..." : mode === "edit" ? "Update Poster" : "Create Poster"}
              </button>
            </div>
          </div>
        </div>
      </ModalShell>
    </>
  );
}

function ConfirmPopup({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onClose,
  danger = false,
}) {
  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      subtitle=""
      width="max-w-[520px]"
      zIndex="z-[1500]"
    >
      <div className="px-6 py-6">
        <p className="text-sm leading-7 text-slate-600">
          {message}
        </p>

        <div className="mt-6 flex items-center justify-end gap-3">
          {cancelText ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-[46px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              style={{ borderColor: "#dfe5ee" }}
            >
              {cancelText}
            </button>
          ) : null}

          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-[46px] items-center justify-center rounded-[14px] px-5 text-sm font-semibold text-white transition"
            style={{
              backgroundColor: danger ? "#dc2626" : "#d86a78",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function PosterStatsModal({ open, item, onClose }) {
  const [loading, setLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !item?.id) return;

    let alive = true;
    setLoading(true);
    setError("");

    postersService
      .stats(item.id)
      .then((res) => {
        if (!alive) return;
        setStatsData(res?.data || null);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || "Unable to load poster stats");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, item]);

  const stats = statsData?.stats || {};

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Poster Stats"
      subtitle="Poster performance Quick Overview"
      width="max-w-[700px]"
    >
      <div className="min-w-0 space-y-5 px-5 py-5 sm:px-6">
        {loading ? <div className="py-12 text-center text-sm text-slate-500">Loading stats...</div> : null}
        {error ? (
          <div className="rounded-[16px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        ) : null}
        {!loading && !error ? (
          <>
            <div className="min-w-0 rounded-[20px] border bg-[#fff8f9] p-4" style={{ borderColor: BORDER }}>
              <div className="break-words text-lg font-bold text-slate-900">
                {item?.title || "-"}
              </div>

              <div className="mt-2 break-all whitespace-pre-wrap text-sm text-slate-500">
                {item?.description || "No description"}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["Viewed Users", stats.viewed_users || 0],
                ["Clicked Users", stats.clicked_users || 0],
                ["Total Views", stats.total_views || 0],
                ["Total Clicks", stats.total_clicks || 0],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-[20px] border bg-white p-4"
                  style={{ borderColor: BORDER }}
                >
                  <div className="text-sm text-slate-500">{label}</div>
                  <div className="mt-2 text-[28px] font-bold text-[#d86a78]">{value}</div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </ModalShell>
  );
}

function PosterViewModal({ open, item, onClose, optionsMap }) {
  if (!open || !item) return null;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Poster Details"
      subtitle="Complete details of the poster can be viewed here"
    >
      <div className="grid gap-6 px-5 py-5 sm:px-6 lg:grid-cols-[1fr_1fr]">
        <div
          className="overflow-hidden rounded-[22px] border bg-slate-50"
          style={{ borderColor: BORDER }}
        >
          {item.image_url ? (
            <img
              src={buildPosterImageUrl(item.image_url)}
              alt={item.title}
              className="h-full min-h-[280px] w-full object-cover"
            />
          ) : (
            <div className="flex min-h-[280px] items-center justify-center text-slate-400">
              No image
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-[24px] font-bold text-slate-900">{item.title || "-"}</div>
            <p className="mt-2 text-sm text-slate-500">{item.description || "No description"}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
              <div className="text-xs uppercase text-slate-400">Status</div>
              <div className="mt-2">
                <StatusBadge item={item} />
              </div>
            </div>
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
              <div className="text-xs uppercase text-slate-400">Target</div>
              <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
                <div className="text-xs uppercase text-slate-400">Placement</div>
                <div className="mt-2 text-sm font-semibold text-slate-700">
                  {item.placement || "TOP"}
                </div>
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-700">
                {item.target_type || "ALL"}
              </div>
            </div>
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
              <div className="text-xs uppercase text-slate-400">Start</div>
              <div className="mt-2 text-sm font-semibold text-slate-700">
                {formatDate(item.start_at)}
              </div>
            </div>
            <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
              <div className="text-xs uppercase text-slate-400">End</div>
              <div className="mt-2 text-sm font-semibold text-slate-700">
                {formatDate(item.end_at)}
              </div>
            </div>
          </div>

          <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
            <div className="mb-2 text-xs uppercase text-slate-400">Audience Filters</div>
            <div className="text-sm text-slate-700">
              <FiltersText filters={item.filters} optionsMap={optionsMap} />
            </div>
          </div>

          <div className="rounded-[18px] border p-4" style={{ borderColor: BORDER }}>
            <div className="mb-2 text-xs uppercase text-slate-400">CTA Link</div>
            <div className="break-all text-sm text-slate-700">{item.cta_link || "-"}</div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

export default function PostersPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [statsItem, setStatsItem] = useState(null);
  const [busyId, setBusyId] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState(null);

  const [filterOptionsMap, setFilterOptionsMap] = useState({
    warehouses: {},
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const activeCount = useMemo(
    () => items.filter((item) => getPosterComputedStatus(item) === "ACTIVE").length,
    [items]
  );

  const filteredCount = useMemo(
    () => items.filter((item) => item.target_type === "FILTERED").length,
    [items]
  );

  const visiblePages = useMemo(
    () => getVisiblePages(page, totalPages),
    [page, totalPages]
  );

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await postersService.list({
        page,
        limit,
        q,
        isActive: statusFilter,
        context: sectionFilter,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (err) {
      setError(err?.message || "Unable to load posters");
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, statusFilter, sectionFilter]);

  const loadFilterMaps = React.useCallback(async () => {
    try {
      const res = await postersService.getFilterOptions();
      const raw = res?.data || res;
      const data = raw?.data || raw || {};
      const normalized = normalizeFilterOptions(data);

      setFilterOptionsMap({
        warehouses: Object.fromEntries(
          (normalized.warehouses || []).map((item) => [item.value, item.label])
        ),
      });
    } catch {
      setFilterOptionsMap({
        warehouses: {},
      });
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadFilterMaps();
  }, [loadFilterMaps]);

  const handleDelete = async (item) => {
    setConfirmDelete(item);
  };

  const handleToggleStatus = async (item) => {
    setConfirmStatus(item);
  };

  const handleReload = async () => {
    await Promise.all([loadData(), loadFilterMaps()]);
  };
const truncatePosterDescription = (text = "", maxWords = 10, maxChars = 45) => {
  const cleanText = String(text || "").trim();
  if (!cleanText) return "";

  const words = cleanText.split(/\s+/);
  let result = words.slice(0, maxWords).join(" ");

  if (result.length > maxChars) {
    result = result.slice(0, maxChars).trimEnd();
  }

  const wasTrimmed =
    words.length > maxWords || cleanText.length > result.length;

  return wasTrimmed ? `${result}...` : result;
};
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden border-0 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
        <div className="px-4 py-4 sm:px-5 lg:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-1.5 text-xs font-semibold text-[#d86a78]">
                <Megaphone size={16} />
                Poster Campaigns
              </div>

              <h1 className="mt-3 text-[24px] font-bold leading-tight text-[#d86a78] sm:text-[28px]">
                Posters
              </h1>

              <p className="mt-1 max-w-[760px] text-sm leading-6 text-slate-500">
                Manage and monitor your visual marketing campaigns across all platforms.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[14px] px-4 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(216,106,120,0.20)] transition hover:-translate-y-0.5"
              style={{ backgroundColor: PINK }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = PINK_HOVER)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = PINK)}
            >
              <Plus size={18} />
              New Poster
            </button>
          </div>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[18px] border bg-white p-4 shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#f7d7dc" }}>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
              <LayoutGrid size={24} />
            </div>
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.04em] text-slate-500">
                Total Posters
              </div>
            </div>
          </div>
          <div className="mt-4 text-[30px] font-bold leading-none text-slate-900">{total}</div>
          <div className="mt-2 text-xs font-medium text-emerald-600">Library overview</div>
        </div>

        <div className="rounded-[18px] border bg-white p-4 shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#d8f0e3" }}>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#edf9f1] text-emerald-600">
              <Check size={24} />
            </div>
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.04em] text-slate-500">
                Active Posters
              </div>
            </div>
          </div>
          <div className="mt-4 text-[30px] font-bold leading-none text-slate-900">{activeCount}</div>
          <div className="mt-2 text-xs font-medium text-slate-500">Active on current page</div>
        </div>

        <div className="rounded-[18px] border bg-white p-4 shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#dfe7fb" }}>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eef3ff] text-[#4f7cff]">
              <SlidersHorizontal size={24} />
            </div>
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.04em] text-slate-500">
                Filtered
              </div>
            </div>
          </div>
          <div className="mt-4 text-[30px] font-bold leading-none text-slate-900">{filteredCount}</div>
          <div className="mt-2 text-xs font-medium text-slate-500">Matching current page data</div>
        </div>

        <div className="rounded-[18px] border bg-white p-4 shadow-[0_8px_28px_rgba(15,23,42,0.04)]" style={{ borderColor: "#fde7c8" }}>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fff5e8] text-[#f59e0b]">
              <FileText size={24} />
            </div>
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.04em] text-slate-500">
                Current Page
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-end gap-2">
            <span className="text-[30px] font-bold leading-none text-slate-900">{page}</span>
            <span className="pb-1 text-lg text-slate-400">/ {totalPages}</span>
          </div>
          <div className="mt-2 text-xs font-medium text-slate-500">Displaying {limit} per page</div>
        </div>
      </div>

      <Card className="overflow-hidden border-0 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
        <div
          className="flex flex-col gap-4 border-b bg-[#fcfcfd] px-5 py-5 sm:px-6"
          style={{ borderColor: "#eef2f7" }}
        >
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative w-full xl:max-w-[520px]">
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setPage(1);
                      setQ(searchInput.trim());
                    }
                  }}
                  placeholder="Search by title, description or campaign details..."
                  className="h-[44px] w-full rounded-[14px] border bg-white py-3 pl-12 pr-4 text-sm text-slate-700 outline-none transition focus:ring-2 focus:ring-[#f7d4da]"
                  style={{ borderColor: "#e7eaf0" }}
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
                className="h-[44px] min-w-[150px] rounded-[14px] border bg-white px-4 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-[#f7d4da]"
                style={{ borderColor: "#e7eaf0" }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              <select
                value={sectionFilter}
                onChange={(e) => {
                  setPage(1);
                  setSectionFilter(e.target.value);
                }}
                className="h-[44px] min-w-[190px] rounded-[14px] border bg-white px-4 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-[#f7d4da]"
                style={{ borderColor: "#e7eaf0" }}
              >
                <option value="">All Sections</option>
                <option value="GROCERY">Grocery only</option>
                <option value="FOOD">Food only</option>
                <option value="ALL">Grocery + Food</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setPage(1);
                  setQ(searchInput.trim());
                }}
                className="inline-flex h-[44px] items-center justify-center gap-2 rounded-[14px] border bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                style={{ borderColor: "#e7eaf0" }}
              >
                <Search size={16} />
                Search
              </button>

              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setQ("");
                  setStatusFilter("");
                  setSectionFilter("");
                  setPage(1);
                }}
                className="inline-flex h-[44px] items-center justify-center gap-2 rounded-[14px] border bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                style={{ borderColor: "#e7eaf0" }}
              >
                <X size={16} />
                Reset
              </button>
            </div>
          </div>
        </div>

        {error ? <div className="px-6 py-4 text-sm text-red-600">{error}</div> : null}

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead>
              <tr className="border-b bg-white text-slate-500" style={{ borderColor: "#eef2f7" }}>
                <th className="px-4 py-3 text-[13px] font-semibold">Poster</th>
                <th className="px-4 py-3 text-[13px] font-semibold">Target Audience</th>
                <th className="px-4 py-3 text-[13px] font-semibold">Schedule</th>
                <th className="px-4 py-3 text-[13px] font-semibold">Placement</th>
                <th className="px-4 py-3 text-[13px] font-semibold">Status</th>
                <th className="px-4 py-3 text-[13px] font-semibold">Created</th>
                <th className="w-[172px] px-2 py-3 text-center text-[13px] font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">
                    Loading posters...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center">
                    <div className="mx-auto flex max-w-[360px] flex-col items-center">
                      <div className="rounded-full bg-[#fff4f6] p-4 text-[#d86a78]">
                        <ImagePlus size={28} />
                      </div>
                      <div className="mt-4 text-lg font-bold text-slate-800">No posters found</div>
                      <div className="mt-1 text-sm text-slate-500">
                        Create your first poster to start using this module.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const disabled = busyId === item.id;

                  return (
                    <tr
                      key={item.id}
                      className="border-b align-top transition hover:bg-[#fcfcfd]"
                      style={{ borderColor: "#eef2f7" }}
                    >
                      <td className="px-4 py-3">
                         <div className="flex min-w-[210px] items-start gap-3">
                          <div
                            className="h-[58px] w-[58px] overflow-hidden rounded-[14px] border bg-slate-50 shadow-sm"
                            style={{ borderColor: "#edf0f5" }}
                          >
                            {item.image_url ? (
                              <img
                                src={buildPosterImageUrl(item.image_url)}
                                alt={item.title}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-slate-400">
                                -
                              </div>
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="text-[15px] font-semibold text-slate-800">
                              {item.title || "-"}
                            </div>

                            <div
  className="mt-1 max-w-[340px] text-sm leading-6 text-slate-500"
  title={item.description || ""}
>
  {item.description
    ? truncatePosterDescription(item.description, 10, 20)
    : "No description"}
</div>

                            {/* {item.cta_link ? (
                              <div className="mt-2 break-all text-xs font-medium text-[#d86a78]">
                                {item.cta_link}
                              </div>
                            ) : null} */}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="min-w-[170px] space-y-2">
                          <div
                            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold ${item.target_type === "FILTERED"
                              ? "bg-[#eef3ff] text-[#4f7cff]"
                              : "bg-slate-100 text-slate-600"
                              }`}
                          >
                            {item.target_type === "FILTERED" ? <MapPin size={13} /> : <Globe2 size={13} />}
                            {item.target_type === "FILTERED" ? "Filtered" : "All Users"}
                          </div>

                          <div className="text-sm leading-6 text-slate-500">
                            <FiltersText filters={item.filters} optionsMap={filterOptionsMap} />
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="min-w-[150px]">
                          <div className="text-sm font-semibold text-slate-700">
                            {item.start_at ? formatDate(item.start_at) : "-"}
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {item.end_at ? formatDate(item.end_at) : "No end date"}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-[#fff1f3] px-3 py-1 text-xs font-semibold text-[#d86a78]">
                          {item.placement || "TOP"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge item={item} />
                      </td>

                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatDate(item.created_at)}
                      </td>

                      <td className="w-[172px] px-2 py-3">
                        <div className="mx-auto inline-flex items-center overflow-hidden rounded-[10px] border border-[#e7eaf0] bg-white shadow-[0_3px_10px_rgba(15,23,42,0.04)]">
                          <button
                            type="button"
                            onClick={() => setViewItem(item)}
                            className="grid h-8 w-8 place-items-center border-r border-[#e7eaf0] text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
                            title="View"
                          >
                            <Eye size={14} />
                          </button>

                          <button
                            type="button"
                            onClick={() => setEditItem(item)}
                            className="grid h-8 w-8 place-items-center border-r border-[#e7eaf0] text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>

                          <button
                            type="button"
                            onClick={() => setStatsItem(item)}
                            className="grid h-8 w-8 place-items-center border-r border-[#e7eaf0] text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
                            title="Stats"
                          >
                            <BarChart3 size={14} />
                          </button>

                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => handleToggleStatus(item)}
                            className="grid h-8 w-8 place-items-center border-r border-[#e7eaf0] text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60"
                            title={item.is_active ? "Deactivate" : "Activate"}
                          >
                            <Power size={14} />
                          </button>

                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => handleDelete(item)}
                            className="grid h-8 w-8 place-items-center text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div
          className="flex flex-col gap-3 border-t bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
          style={{ borderColor: "#eef2f7" }}
        >
          <div className="text-sm text-slate-500">
            Showing {items.length ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} results
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="flex h-9 w-9 items-center justify-center rounded-[12px] border bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: "#e7eaf0" }}
            >
              <ChevronLeft size={18} />
            </button>

            {visiblePages.map((item, index) =>
              item === "..." ? (
                <div
                  key={`ellipsis-${index}`}
                  className="flex h-9 min-w-[36px] items-center justify-center rounded-[12px] text-slate-400"
                >
                  <MoreHorizontal size={18} />
                </div>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPage(item)}
                  className={`h-11 min-w-[44px] rounded-[14px] px-3 text-sm font-semibold transition ${page === item
                    ? "text-white shadow-[0_12px_24px_rgba(216,106,120,0.24)]"
                    : "border bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  style={
                    page === item
                      ? { backgroundColor: PINK }
                      : { borderColor: "#e7eaf0" }
                  }
                >
                  {item}
                </button>
              )
            )}

            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              className="flex h-9 w-9 items-center justify-center rounded-[12px] border bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: "#e7eaf0" }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </Card>

      <PosterFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleReload}
      />

      <PosterFormModal
        open={!!editItem}
        mode="edit"
        item={editItem}
        onClose={() => setEditItem(null)}
        onSaved={handleReload}
      />

      <PosterViewModal
        open={!!viewItem}
        item={viewItem}
        onClose={() => setViewItem(null)}
        optionsMap={filterOptionsMap}
      />

      <PosterStatsModal
        open={!!statsItem}
        item={statsItem}
        onClose={() => setStatsItem(null)}
      />

      <ConfirmPopup
        open={!!confirmDelete}
        title="Delete Poster"
        message={`Are you sure you want to delete "${confirmDelete?.title || ""}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete?.id) return;

          try {
            setBusyId(confirmDelete.id);
            await postersService.remove(confirmDelete.id);
            setConfirmDelete(null);
            await handleReload();
          } catch (err) {
            window.alert(err?.message || "Unable to delete poster");
          } finally {
            setBusyId("");
          }
        }}
      />

      <ConfirmPopup
        open={!!confirmStatus}
        title={confirmStatus?.is_active ? "Deactivate Poster" : "Activate Poster"}
        message={
          confirmStatus?.is_active
            ? "Users will stop seeing this poster."
            : "Users will start seeing this poster."
        }
        confirmText="Confirm"
        cancelText="Cancel"
        onClose={() => setConfirmStatus(null)}
        onConfirm={async () => {
          if (!confirmStatus?.id) return;

          try {
            setBusyId(confirmStatus.id);
            await postersService.setStatus(
              confirmStatus.id,
              !confirmStatus.is_active
            );
            setConfirmStatus(null);
            await handleReload();
          } catch (err) {
            window.alert(err?.message || "Unable to update status");
          } finally {
            setBusyId("");
          }
        }}
      />
    </div>
  );
}
